import { ApiError, type ShopperExperience } from '@/scapi';

export type Page = ShopperExperience.schemas['Page'];

export type ContentSlotPageWithComponentData = Page & {
    componentData?: Record<string, Promise<unknown>>;
};

/** JSON-serializable page shape returned from fetcher resource routes. */
export type SerializableContentSlotPage = Omit<ContentSlotPageWithComponentData, 'componentData'> & {
    componentData?: Record<string, unknown>;
};

/** Empty page used when an optional content-slot page is not configured in Page Designer yet. */
export function createEmptyContentSlotPage(pageId: string): ContentSlotPageWithComponentData {
    return {
        id: pageId,
        typeId: 'page',
        regions: [],
        componentData: {},
    };
}

export function createEmptySerializableContentSlotPage(pageId: string): SerializableContentSlotPage {
    return {
        id: pageId,
        typeId: 'page',
        regions: [],
        componentData: {},
    };
}

/** True when Shopper Experience reports the page id does not exist (optional slot pages). */
export function isContentSlotPageNotFoundError(error: unknown): boolean {
    return getErrorHttpStatus(error) === 404;
}

/**
 * True when a content-slot page fetch can degrade to an empty page instead of failing the route.
 * Covers missing pages (404) and transient upstream failures (5xx, e.g. SCAPI 521).
 */
export function isContentSlotPageRecoverableFetchError(error: unknown): boolean {
    const status = getContentSlotFetchErrorStatus(error);
    if (status === undefined) {
        return false;
    }

    return status === 404 || status >= 500;
}

export function getContentSlotFetchErrorStatus(error: unknown): number | undefined {
    const status = getErrorHttpStatus(error);
    if (status !== undefined) {
        return status;
    }

    return parseApiErrorStatusFromMessage(error);
}

function parseApiErrorStatusFromMessage(error: unknown): number | undefined {
    if (!(error instanceof Error)) {
        return undefined;
    }

    const match = error.message.match(/API Error (\d{3})/);
    if (!match?.[1]) {
        return undefined;
    }

    return Number.parseInt(match[1], 10);
}

function getErrorHttpStatus(error: unknown): number | undefined {
    if (error instanceof ApiError && typeof error.status === 'number') {
        return error.status;
    }

    if (error instanceof Response) {
        return error.status;
    }
    if (typeof error !== 'object' || error === null) {
        return undefined;
    }

    if ('status' in error && typeof error.status === 'number') {
        return error.status;
    }

    if ('statusCode' in error && typeof error.statusCode === 'number') {
        return error.statusCode;
    }

    return undefined;
}

/** Re-wraps resolved component data as promises for Region rendering on the client. */
export function hydrateContentSlotPageComponentData(
    page: SerializableContentSlotPage
): ContentSlotPageWithComponentData {
    const componentData: Record<string, Promise<unknown>> = {};

    if (page.componentData) {
        for (const [id, value] of Object.entries(page.componentData)) {
            componentData[id] = Promise.resolve(value);
        }
    }

    return { ...page, componentData };
}

export function contentSlotRegionHasComponents(
    page: ContentSlotPageWithComponentData | undefined,
    regionId: string
): boolean {
    const region = page?.regions?.find((entry) => entry.id === regionId);
    return Boolean(region?.components?.length);
}
